import { useRef, useMemo, useEffect, useState } from 'react';
import {
  generatePath,
  Outlet,
  useOutletContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { UseFormHandleSubmit, useForm } from 'react-hook-form';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useScroll,
  Filters,
  formatDate,
  SchemaFilterType,
  FilterTag,
  Sort,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { blacklistUsersColumns } from './variables/user';
import { BlacklistData } from '../types';
import { setSkipValue } from './store/slices/list';

import ROUTES from '../../routes';

import cn from './blacklist.module.css';

const formatBlacklistUsers = (data: BlacklistData[]): BlacklistData[] => {
  const nextList = data?.map((item) => {
    const result = { ...item };
    const currentUser = data?.find((user) => user.id === result.id);

    result.url = '';

    if (currentUser) {
      result.url = generatePath(ROUTES.BLACKLIST_USER, {
        userId: result.id,
        blocklistId: currentUser.source?.blacklistId,
      });
    }

    const render: Record<string, React.JSX.Element> = {};

    render.createdAt = (
      <div>{item.createdAt ? formatDate(item.createdAt, true) : '—'}</div>
    );
    render.lastUpdated = (
      <div>{item?.lastUpdated ? formatDate(item.lastUpdated, true) : '—'}</div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const BlacklistUsers = () => {
  const [entityId, setEntityId] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { search } = useLocation();

  const {
    isFetching,
    isLoading,
    errorList,
    onSort,
    filters,
    sort,
    onFilter,
    setPage,
    takeCount,
    setTakeCount,
  } = useOutletContext<{
    isFetching?: boolean;
    isLoading?: boolean;
    errorList?: string;
    onSort: (name: string) => void;
    filters: Filters;
    sort: Sort;
    onFilter: (values: Filters, withoutTimeZone?: boolean) => void;
    setPage: ({
      id,
      take,
      filters,
      withSkip,
    }: {
      id: string;
      take: string;
      filters: Filters;
      withSkip?: boolean;
    }) => void;
    takeCount: number;
    setTakeCount: (data: number) => void;
  }>();

  const {
    blacklist: {
      list: {
        searchResult,
        searchResultPagination,
        skipValue,
        searchResultErrorsList,
      },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { handleSubmit } = useForm();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formattedData = useMemo(() => {
    const data = formatBlacklistUsers(searchResult as BlacklistData[]);

    return data;
  }, [searchResult]);

  useScroll({ element: scrollRef, requestInProgress: isFetching as boolean });

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.entityGUID) {
        setEntityId(parsed.entityGUID as string);
      }
    }
  }, [search]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        setTakeCount(Math.ceil(take + 3));
      }
    }
  }, [scrollRef]);

  let content: React.JSX.Element;

  if (isLoading || takeCount === 0) {
    content = (
      <div className={cn.blacklistLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={blacklistUsersColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={{}}
        onFilterChange={(filterValues) =>
          onFilter({
            ...filterValues,
            take: {
              value: [takeCount.toString()],
              type: SchemaFilterType.STRING,
            },
          })
        }
        handleSubmit={handleSubmit as UseFormHandleSubmit<Filters>}
        isLoading={isFetching}
        tableClassName={cn.tableContent}
        take={takeCount.toString()}
        error={searchResultErrorsList || errorList}
        setPage={(data) => {
          let nextSkip = skipValue as number;

          if (Number(data.take) > 0) {
            nextSkip += Number(data.take);
          } else {
            nextSkip -= Math.abs(Number(data.take));
          }

          dispatch(setSkipValue(nextSkip));

          setPage({
            ...data,
            id: nextSkip?.toString(),
            filters: {
              ...filters,
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            },
            withSkip: true,
          });
        }}
        pagination={searchResultPagination}
        tableFormClassName={cn.usersTable}
        clickable={Boolean(
          activePermissions?.includes(PermissionEnum.BLACKLISTS_ITEMS_LIST),
        )}
      />
    );
  }

  return (
    <>
      <div className={cn.blacklistTableWrapper} ref={scrollRef}>
        {entityId && (
          <div className={cn.entityIdTag}>
            <FilterTag
              title="Entity GUID:"
              value={entityId}
              type="round"
              onClick={() => {
                setEntityId('');
                navigate(ROUTES.BLACKLIST_LIST_RESULTS);
              }}
            />
          </div>
        )}
        {content}
      </div>
      <Outlet context={{ getBlacklistAction: undefined }} />
    </>
  );
};

export default BlacklistUsers;
